import React, { useState, useRef, useEffect } from 'react';

const panelStyle = {
  width: 280,
  background: '#1a1a2e',
  borderLeft: '1px solid rgba(102,126,234,0.15)',
  padding: 16,
  overflowY: 'auto',
  color: '#eee',
  fontFamily: 'sans-serif',
  borderRadius: '8px 0 0 8px',
};

const sectionStyle = {
  marginBottom: 16,
  paddingBottom: 12,
  borderBottom: '1px solid #222',
};

const sliderRow = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 6,
};

const sliderLabel = {
  fontSize: 11,
  color: '#888',
  width: 70,
};

const sliderInput = {
  flex: 1,
  accentColor: '#667eea',
};

const valLabel = {
  fontSize: 11,
  color: '#ccc',
  width: 36,
  textAlign: 'right',
};

/**
 * AdjustmentsPanel — brightness/contrast, sharpening, vignette.
 * All sections auto-apply on slider change (debounced 500ms). No Apply buttons.
 */
export default function AdjustmentsPanel({ editor }) {
  // Brightness / Contrast
  const [brightness, setBrightness] = useState(0);
  const [contrast, setContrast] = useState(0);

  // Sharpening
  const [sharpAmount, setSharpAmount] = useState(1.0);
  const [sharpRadius, setSharpRadius] = useState(1.0);
  const [sharpThreshold, setSharpThreshold] = useState(0);

  // Vignette
  const [vigIntensity, setVigIntensity] = useState(0.5);
  const [vigRadius, setVigRadius] = useState(1.0);

  // Mount guards
  const bcMountedRef = useRef(false);
  const sharpMountedRef = useRef(false);
  const vigMountedRef = useRef(false);

  // Debounce timers
  const bcTimerRef = useRef(null);
  const sharpTimerRef = useRef(null);
  const vigTimerRef = useRef(null);

  // Auto-apply brightness/contrast
  useEffect(() => {
    if (!bcMountedRef.current) {
      bcMountedRef.current = true;
      return;
    }
    if (bcTimerRef.current) clearTimeout(bcTimerRef.current);
    bcTimerRef.current = setTimeout(() => {
      editor.brightnessContrast({ brightness, contrast });
    }, 500);
    return () => clearTimeout(bcTimerRef.current);
  }, [brightness, contrast]);

  // Auto-apply sharpening
  useEffect(() => {
    if (!sharpMountedRef.current) {
      sharpMountedRef.current = true;
      return;
    }
    if (sharpTimerRef.current) clearTimeout(sharpTimerRef.current);
    sharpTimerRef.current = setTimeout(() => {
      editor.sharpen({
        amount: sharpAmount,
        radius: sharpRadius,
        threshold: sharpThreshold,
      });
    }, 500);
    return () => clearTimeout(sharpTimerRef.current);
  }, [sharpAmount, sharpRadius, sharpThreshold]);

  // Auto-apply vignette
  useEffect(() => {
    if (!vigMountedRef.current) {
      vigMountedRef.current = true;
      return;
    }
    if (vigTimerRef.current) clearTimeout(vigTimerRef.current);
    vigTimerRef.current = setTimeout(() => {
      editor.vignette({ intensity: vigIntensity, radius: vigRadius });
    }, 500);
    return () => clearTimeout(vigTimerRef.current);
  }, [vigIntensity, vigRadius]);

  return (
    <div style={panelStyle}>
      <h3 style={{ margin: '0 0 12px', fontSize: 14, color: '#ddd' }}>☀️ Adjustments</h3>
      <div style={{ fontSize: 10, color: '#555', marginBottom: 12 }}>
        Auto-applies as you adjust
      </div>

      {/* Brightness / Contrast */}
      <div style={sectionStyle}>
        <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 8, color: '#aaa' }}>
          Brightness &amp; Contrast
        </div>
        <div style={sliderRow}>
          <span style={sliderLabel}>Brightness</span>
          <input
            type="range" min={-100} max={100} value={brightness}
            onChange={(e) => setBrightness(Number(e.target.value))}
            style={sliderInput}
          />
          <span style={valLabel}>{brightness}</span>
        </div>
        <div style={sliderRow}>
          <span style={sliderLabel}>Contrast</span>
          <input
            type="range" min={-100} max={100} value={contrast}
            onChange={(e) => setContrast(Number(e.target.value))}
            style={sliderInput}
          />
          <span style={valLabel}>{contrast}</span>
        </div>
      </div>

      {/* Sharpening */}
      <div style={sectionStyle}>
        <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 8, color: '#aaa' }}>
          Sharpening
        </div>
        <div style={sliderRow}>
          <span style={sliderLabel}>Amount</span>
          <input
            type="range" min={0} max={5} step={0.1} value={sharpAmount}
            onChange={(e) => setSharpAmount(Number(e.target.value))}
            style={sliderInput}
          />
          <span style={valLabel}>{sharpAmount.toFixed(1)}</span>
        </div>
        <div style={sliderRow}>
          <span style={sliderLabel}>Radius</span>
          <input
            type="range" min={0} max={20} step={0.5} value={sharpRadius}
            onChange={(e) => setSharpRadius(Number(e.target.value))}
            style={sliderInput}
          />
          <span style={valLabel}>{sharpRadius.toFixed(1)}</span>
        </div>
        <div style={sliderRow}>
          <span style={sliderLabel}>Threshold</span>
          <input
            type="range" min={0} max={255} value={sharpThreshold}
            onChange={(e) => setSharpThreshold(Number(e.target.value))}
            style={sliderInput}
          />
          <span style={valLabel}>{sharpThreshold}</span>
        </div>
      </div>

      {/* Vignette */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 8, color: '#aaa' }}>
          Vignette
        </div>
        <div style={sliderRow}>
          <span style={sliderLabel}>Intensity</span>
          <input
            type="range" min={0} max={1} step={0.05} value={vigIntensity}
            onChange={(e) => setVigIntensity(Number(e.target.value))}
            style={sliderInput}
          />
          <span style={valLabel}>{vigIntensity.toFixed(2)}</span>
        </div>
        <div style={sliderRow}>
          <span style={sliderLabel}>Radius</span>
          <input
            type="range" min={0.1} max={2} step={0.05} value={vigRadius}
            onChange={(e) => setVigRadius(Number(e.target.value))}
            style={sliderInput}
          />
          <span style={valLabel}>{vigRadius.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}
